import { GuaranteeDto } from "api/pricing";
import { GenericParameter } from "components/display/GenericParameter";
import { InfoIcon } from "components/icons/Icons";
import { Toggle } from "components/forms/Toggle";
import { GuaranteesValuesWarning } from "../../hooks/useContractGuarantees";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";

interface GuaranteCardItemProps {
  guarantee: GuaranteeDto;
  isRequired?: boolean;
  onDeductibleChange?: (value: number) => void;
  onCeilingChange?: (value: number) => void;
  deductible?: GuaranteesValuesWarning;
  ceiling?: GuaranteesValuesWarning;
  isActive: boolean;
  toggleGuarantee: () => void;
  showGuaranteeDetails: (title: string, longDescription: string) => void;
}

export const GuaranteeCardItem: React.FC<GuaranteCardItemProps> = ({
  guarantee,
  isRequired = true,
  onDeductibleChange,
  onCeilingChange,
  deductible,
  ceiling,
  toggleGuarantee,
  isActive,
  showGuaranteeDetails
}) => {
  const { t } = useDynamicTranslation([NsKey.Pricing]);
  const hasDeductible = guarantee.deductible !== undefined && guarantee.deductible !== null;
  const hasCeiling = guarantee.ceiling !== undefined && guarantee.ceiling !== null;

  return (
    <div className={`border flex bg-white rounded-2xl transition-all duration-200 ${isActive
      ? 'border-lime-500'
      : 'border-gray-200 bg-white hover:border-gray-300'
      }`}>
      <div className="grow flex items-center p-4 gap-6">
        <Toggle
          value={isActive}
          disabled={isRequired}
          onChange={() => toggleGuarantee()}
        />
        <div className="grow">
          <h3 className="font-semibold text-gray-900">{guarantee.name}</h3>
          <p className="text-gray-600 text-sm">{guarantee.shortDescription}</p>
        </div>

        {/* Afficher les inputs seulement si les propriétés existent */}
        {(hasDeductible || hasCeiling) && (
          <div className="flex-none flex gap-4">
            {hasDeductible && deductible && (
              <GenericParameter
                title="Franchise"
                placeholder="1 500"
                parameters={deductible.parameters}
                value={Number(deductible.value)}
                warningMessage={deductible.warningMessage}
                onChange={(value) => onDeductibleChange && onDeductibleChange(Number(value))}
              />
            )}
            {hasCeiling && ceiling && (
              <GenericParameter
                title="Plafond"
                placeholder="6 000 000"
                parameters={ceiling.parameters}
                value={Number(ceiling.value)}
                warningMessage={ceiling.warningMessage}
                onChange={(value) => onCeilingChange && onCeilingChange(Number(value))}
              />
            )}
          </div>
        )}
      </div>
      <div className="flex-none w-36 p-2 gap-4 flex flex-col justify-between items-end">
        <div className={`text-xs rounded-full py-1 px-3 whitespace-nowrap ${isActive ? "bg-lime-100 text-lime-500" : "bg-red-100 text-red-500"
          }`}>
          {isActive ? t('PRICING.GUARANTEES.SELECTED') : t('PRICING.GUARANTEES.NOT_SELECTED')}
        </div>
        <div onClick={() => guarantee.longDescription && guarantee.name && showGuaranteeDetails(guarantee.name, guarantee.longDescription)} >
          <InfoIcon className="w-5 h-5 -mb-1 text-primary cursor-pointer" />
        </div>
      </div>
    </div>
  );
};
